import React, { Dispatch, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useLocation } from 'react-router-dom';
import ProjectSelectionView from './components/Views/ProjectSelectionView/ProjectSelectionView';
import ProjectView from './components/Views/ProjectView/ProjectView';
import DocumentLoaderView, {
    SliceNameEnum,
} from './components/Views/DocumentLoaderView/DocumentLoaderView';
import { getSettings } from './redux/user/sliceUserSettings';
import DocumentDetailsView from './components/Views/DocumentDetailsView/DocumentDetailsView';
import ProjectViewError from './components/Views/ProjectView/ProjectViewError';
import { IAppReduxStore } from './redux/store';
import ProjectViewOrImportView from './components/Views/ProjectViewOrImportView';
import SessionTimeOut from './components/General/SessionTimeOut/SessionTimeOut';
import { getAllStamps } from './redux/user/sliceUserStamps';
import { useEnqueueSnackbar } from './hooks/useEnqueueSnackbar';
import { ToastSeverity } from './components/General/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { useDynamicBrowserTitle } from './hooks/useDynamicBrowserTitle';
import AppVersionIndicator from './components/General/AppVersionIndicator.tsx/AppVersionIndicator';
import UploadNotifications from './components/General/UploadNotifications/UploadNotifications';
import { ErrorBoundary } from 'react-error-boundary';
import GeneralErrorView from './components/Views/ErrorView/GeneralErrorView';
import { generalError } from './redux/general/sliceGeneralErrorHandle';
import useActionPending from './hooks/useActionPending';

export default () => {
    /** Allows reducers on redux slices to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const { t } = useTranslation('translations', { keyPrefix: 'app' });

    const route = useLocation();

    // US7315
    useActionPending();

    const userSettingsError = useSelector((store: IAppReduxStore) => store.userSettings.error);
    const generalErrorRequestError = useSelector(
        (store: IAppReduxStore) => store.generalError.error,
    );

    /** Hook for customizing browser title. */
    const browserTitle = useDynamicBrowserTitle();

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    useEffect(() => {
        window.top.document.title = browserTitle;
    }, [browserTitle]);

    /** Fetch the user's settings when the application loads. */
    useEffect(() => {
        dispatch(getSettings(null));
        dispatch(getAllStamps());
    }, []);

    useEffect(() => {
        if (generalErrorRequestError) {
            enqueueSnackbar(t('generalErrorRequestError'), {
                severity: ToastSeverity.Error,
            });
        }
    }, [generalErrorRequestError]);

    const replicaId = useMemo(() => {
        return /^[a-zA-Z0-9]{16}$/.test(route.pathname.substring(1).split('/')[1])
            ? route.pathname.substring(1).split('/')[1]
            : '';
    }, [route.pathname]);

    return (
        <ErrorBoundary
            fallback={<GeneralErrorView replicaId={replicaId} />}
            onError={(error: Error) => {
                dispatch(
                    generalError({
                        error,
                        replicaId,
                    }),
                );
            }}
        >
            <SessionTimeOut />
            <AppVersionIndicator />
            <UploadNotifications />

            {userSettingsError ? (
                <ProjectViewError />
            ) : (
                <Routes>
                    <Route path='/' element={<ProjectSelectionView />} />
                    <Route
                        path='/document-loader/:replicaId/:universalId/:fileName/:documentFileInfo'
                        element={<DocumentLoaderView sliceName={SliceNameEnum.projectFileOpen} />}
                    />
                    <Route
                        path='/workflow-loader/:replicaId/:universalId/:docNumber'
                        element={<DocumentLoaderView sliceName={SliceNameEnum.workflowHistory} />}
                    />
                    <Route
                        path='/initial-workflow-loader/:replicaId/:universalId/:fileName'
                        element={
                            <DocumentLoaderView sliceName={SliceNameEnum.projectWorkflowInitial} />
                        }
                    />
                    <Route
                        path='/project-documentation/:replicaId/:universalId/:fileName'
                        element={
                            <DocumentLoaderView sliceName={SliceNameEnum.projectDocumentation} />
                        }
                    />
                    <Route
                        path='/comparison-loader/:replicaId/:universalId'
                        element={
                            <DocumentLoaderView
                                sliceName={SliceNameEnum.projectDocumentsComparison}
                            />
                        }
                    />
                    <Route
                        path='/document/:replicaId/:universalId'
                        element={<DocumentDetailsView />}
                    />
                    <Route path='/project' element={<ProjectView />} />
                    <Route path='/project/:replicaId' element={<ProjectView />} />
                    <Route
                        path='/project/:replicaId/:areaName'
                        element={<ProjectViewOrImportView />}
                    />
                    <Route
                        path='/project/:replicaId/:areaName/:viewName'
                        element={<ProjectView />}
                    />
                    <Route
                        path='/project/:replicaId/:areaName/:viewName/:category'
                        element={<ProjectView />}
                    />
                </Routes>
            )}
        </ErrorBoundary>
    );
};
